import { useState, useEffect } from 'react';
import { api } from '../api';

// MyCards — manual card portfolio. User picks which premium cards they
// hold from the catalog; for each, the annual credits surface as a
// utilization tracker. Pilot uses this to suggest using unused credits
// on the planned trip.
//
// Manual flow on purpose — no email parsing, no OAuth. User stays in
// control of what they tell the app.
export default function MyCards() {
  const [catalog, setCatalog] = useState([]);
  const [mine, setMine] = useState([]);
  const [usageMap, setUsageMap] = useState({});
  const [adding, setAdding] = useState(null); // card_key being added
  const [search, setSearch] = useState('');
  const [issuerFilter, setIssuerFilter] = useState('all');

  const reload = async () => {
    const [c, m] = await Promise.all([api.getCardCatalog(), api.getMyCards()]);
    setCatalog(c.cards || []);
    setMine(m.cards || []);
    setUsageMap(m.usageMap || {});
  };
  useEffect(() => { reload(); }, []);

  const isHeld = (key) => mine.some(c => c.card_key === key);

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-4 pb-20">
      <div className="text-center my-6">
        <div className="text-brand-ink/55 text-xs font-bold uppercase tracking-[0.3em]">MY CARDS</div>
        <h1 className="text-2xl sm:text-3xl font-black mt-1 text-brand-ink">Card Portfolio</h1>
        <p className="text-sm text-brand-ink/70 mt-1 max-w-lg mx-auto">
          Tell Pilot which premium cards you hold. Pilot uses unused annual credits
          ($200 Amex airline, $300 Sapphire travel, etc.) when planning your trip —
          so you don't leave $$$ on the table.
        </p>
      </div>

      {/* Stats summary */}
      {mine.length > 0 && (
        <CardPortfolioSummary mine={mine} usageMap={usageMap} />
      )}

      {/* Cards you hold */}
      {mine.length > 0 && (
        <div className="mb-6">
          <h2 className="text-base font-black text-brand-ink mb-2">Cards you hold ({mine.length})</h2>
          <div className="space-y-2">
            {mine.map(card => (
              <HeldCard key={card.card_key} card={card} usageMap={usageMap} onChange={reload} />
            ))}
          </div>
        </div>
      )}

      {/* Add a card — searchable + filterable directory */}
      <div>
        <h2 className="text-base font-black text-brand-ink mb-2">Add a card</h2>

        <div className="mb-2 relative">
          <input
            type="text" placeholder="Search by name, issuer, or category (e.g. Sapphire, dining, Bilt)…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg px-3 py-2 text-xs text-brand-ink placeholder-brand-ink/45 focus:border-teal/50 focus:outline-none"
          />
          {/* Autocomplete dropdown — shows matching cards as you type */}
          {search.trim().length >= 1 && (() => {
            const q = search.toLowerCase();
            const matches = catalog
              .filter(c => !isHeld(c.key))
              .filter(c =>
                c.name.toLowerCase().includes(q) ||
                c.issuer.toLowerCase().includes(q) ||
                (c.earning || []).some(e => e.category.toLowerCase().includes(q) || (e.note || '').toLowerCase().includes(q))
              )
              .slice(0, 5);
            if (matches.length === 0) return null;
            return (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-navy-light border border-brand-ink/25 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                {matches.map(c => (
                  <button
                    key={c.key}
                    onClick={async () => {
                      setAdding(c.key);
                      await api.addMyCard({ card_key: c.key });
                      setSearch('');
                      setAdding(null);
                      reload();
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-teal/10 border-b border-brand-ink/10 last:border-b-0"
                  >
                    <div className="text-xs font-bold text-brand-ink">{c.name}</div>
                    <div className="text-[10px] text-brand-ink/65">{c.issuer} · ${c.annual_fee}/yr · {(c.earning || []).slice(0, 3).map(e => `${e.multiplier}x ${e.category}`).join(' · ')}</div>
                  </button>
                ))}
              </div>
            );
          })()}
        </div>

        <div className="flex flex-wrap gap-1 mb-3">
          {['all', 'Amex', 'Chase', 'Capital One', 'Wells Fargo (Bilt)'].map(iss => (
            <button
              key={iss}
              onClick={() => setIssuerFilter(iss)}
              className={`text-[10px] font-bold px-2 py-1 rounded transition ${
                issuerFilter === iss ? 'bg-teal text-navy' : 'bg-brand-ink/10 text-brand-ink/70 hover:bg-brand-ink/15'
              }`}
            >
              {iss === 'all' ? 'All issuers' : iss}
            </button>
          ))}
        </div>

        <div className="space-y-1.5">
          {catalog
            .filter(c => !isHeld(c.key))
            .filter(c => issuerFilter === 'all' || c.issuer === issuerFilter)
            .filter(c => {
              if (!search.trim()) return true;
              const q = search.toLowerCase();
              return c.name.toLowerCase().includes(q) || c.issuer.toLowerCase().includes(q) ||
                (c.earning || []).some(e => e.category.toLowerCase().includes(q) || (e.note || '').toLowerCase().includes(q));
            })
            .map(card => (
            <div key={card.key} className="bg-navy-light border border-brand-ink/15 rounded-lg p-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-brand-ink">{card.name}</div>
                <div className="text-[10px] text-brand-ink/65">
                  {card.issuer} · ${card.annual_fee}/yr AF
                  {card.welcome_bonus_required > 0 && ` · ${(card.welcome_bonus_required).toLocaleString()} pts welcome`}
                </div>
                <div className="text-[10px] text-brand-ink/55 mt-1 line-clamp-2">
                  {card.annual_credits.length} annual credits · {card.annual_credits.slice(0, 3).map(c => c.label.split(' ')[0]).join(', ')}{card.annual_credits.length > 3 ? ` +${card.annual_credits.length - 3} more` : ''}
                </div>
              </div>
              <button
                onClick={async () => {
                  setAdding(card.key);
                  await api.addMyCard({ card_key: card.key });
                  setAdding(null);
                  reload();
                }}
                disabled={adding === card.key}
                className="text-xs font-bold bg-teal text-navy px-3 py-1.5 rounded-lg disabled:opacity-40 shrink-0"
              >
                {adding === card.key ? 'Adding…' : '+ Add'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// CardPortfolioSummary — top-line "leave nothing on the table" scorecard
function CardPortfolioSummary({ mine, usageMap }) {
  let totalCreditValue = 0;
  let totalUsed = 0;
  let annualFees = 0;
  mine.forEach(card => {
    annualFees += card.annual_fee || 0;
    (card.annual_credits || []).forEach(c => {
      totalCreditValue += c.amount;
      totalUsed += usageMap[`${card.card_key}|${c.key}`] || 0;
    });
  });
  const remaining = totalCreditValue - totalUsed;
  const pctUsed = totalCreditValue > 0 ? Math.round((totalUsed / totalCreditValue) * 100) : 0;
  const net = totalUsed - annualFees;

  return (
    <div className="bg-gradient-to-br from-emerald-500/15 to-teal/5 border-2 border-emerald-500/40 rounded-2xl p-4 mb-4">
      <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider mb-2">📊 Your portfolio</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Annual credits" value={`$${totalCreditValue.toLocaleString()}`} />
        <Stat label="Used YTD" value={`$${totalUsed.toLocaleString()}`} accent={pctUsed > 70 ? 'text-emerald-300' : pctUsed > 40 ? 'text-amber-300' : 'text-rose-300'} />
        <Stat label="Still available" value={`$${remaining.toLocaleString()}`} accent="text-teal" />
        <Stat label="Net of fees" value={`${net >= 0 ? '+' : '-'}$${Math.abs(net).toLocaleString()}`} accent={net >= 0 ? 'text-emerald-300' : 'text-rose-300'} />
      </div>
      {remaining > 100 && (
        <div className="mt-2 text-[11px] text-emerald-300 font-bold">
          💡 ${remaining.toLocaleString()} in unused credits this year — Pilot will suggest using them on your next trip.
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-navy-light rounded-md p-2 text-center">
      <div className="text-[9px] uppercase font-bold text-brand-ink/55">{label}</div>
      <div className={`text-base font-black mt-0.5 ${accent || 'text-brand-ink'}`}>{value}</div>
    </div>
  );
}

// HeldCard — a card the user holds. Shows annual credit utilization,
// welcome bonus progress, lets user log "used $X of credit Y today."
function HeldCard({ card, usageMap, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const credits = card.annual_credits || [];
  const totalValue = credits.reduce((s, c) => s + c.amount, 0);
  const totalUsed = credits.reduce((s, c) => s + (usageMap[`${card.card_key}|${c.key}`] || 0), 0);
  const pct = totalValue > 0 ? Math.round((totalUsed / totalValue) * 100) : 0;

  return (
    <div className="bg-navy-light border border-brand-ink/15 rounded-lg overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-brand-ink">{card.card_name}</div>
          <div className="text-[10px] text-brand-ink/65">
            {card.issuer} · ${card.annual_fee}/yr AF · ${totalValue} total credits ({pct}% used)
          </div>
          <div className="h-1.5 bg-brand-ink/10 rounded-full mt-1.5 overflow-hidden">
            <div className={`h-full rounded-full ${pct > 70 ? 'bg-emerald-400' : pct > 40 ? 'bg-amber-400' : 'bg-rose-400'}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
        <span className="text-brand-ink/55 text-xs">{expanded ? '▼' : '▶'}</span>
      </button>
      {expanded && (
        <div className="p-3 border-t border-brand-ink/10 space-y-2">
          {/* Earning rates section */}
          {card.earning && card.earning.length > 0 && (
            <div className="bg-emerald-500/5 border border-emerald-500/25 rounded-lg p-2.5 mb-2">
              <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider mb-1.5">⚡ Earning rates · use this card for these purchases</div>
              <div className="space-y-1">
                {card.earning.map((e, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-2 text-[11px]">
                    <div className="flex-1">
                      <span className="font-bold text-brand-ink">{e.category.replace(/_/g, ' ')}</span>
                      {e.note && <div className="text-[10px] text-brand-ink/65 italic">{e.note}</div>}
                    </div>
                    <span className="text-emerald-300 font-black text-sm">{e.multiplier}x</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="text-[10px] font-bold text-brand-ink/55 uppercase mb-1">Annual credits</div>
          {credits.map(c => {
            const used = usageMap[`${card.card_key}|${c.key}`] || 0;
            const rem = Math.max(0, c.amount - used);
            return <CreditRow key={c.key} cardKey={card.card_key} credit={c} used={used} remaining={rem} onChange={onChange} />;
          })}
          <button
            onClick={async () => {
              if (window.confirm(`Remove ${card.card_name} from your portfolio?`)) {
                await api.removeMyCard(card.card_key);
                onChange();
              }
            }}
            className="text-[10px] text-rose-300/65 hover:text-rose-300 mt-2"
          >
            Remove from portfolio
          </button>
        </div>
      )}
    </div>
  );
}

function CreditRow({ cardKey, credit, used, remaining, onChange }) {
  const [logging, setLogging] = useState(false);
  const [amount, setAmount] = useState('');

  const log = async () => {
    const n = Number(amount);
    if (!n || n <= 0) return;
    setLogging(true);
    try {
      await api.logCreditUsage({ card_key: cardKey, credit_key: credit.key, amount_used: n });
      setAmount('');
      onChange();
    } finally {
      setLogging(false);
    }
  };

  const isDone = remaining === 0;
  return (
    <div className={`bg-brand-ink/5 rounded p-2.5 ${isDone ? 'opacity-60' : ''}`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-brand-ink">{credit.label}</div>
          <div className="text-[10px] text-brand-ink/65">Use on: {credit.use_on}</div>
        </div>
        <div className="text-right">
          <div className={`text-xs font-black ${isDone ? 'text-emerald-300' : remaining < credit.amount * 0.3 ? 'text-amber-300' : 'text-brand-ink'}`}>
            {isDone ? '✓ Used' : `$${remaining} left`}
          </div>
          <div className="text-[9px] text-brand-ink/55">of ${credit.amount}</div>
        </div>
      </div>
      {!isDone && (
        <div className="mt-2 flex items-center gap-1.5">
          <span className="text-[10px] text-brand-ink/65">Log usage:</span>
          <span className="text-[10px] text-brand-ink/55">$</span>
          <input
            type="number" min={1} max={remaining}
            value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder={String(remaining)}
            className="w-16 bg-brand-ink/5 border border-brand-ink/15 rounded px-1.5 py-0.5 text-[11px] text-brand-ink"
          />
          <button onClick={log} disabled={logging || !amount} className="text-[10px] font-bold bg-teal/20 text-teal hover:bg-teal hover:text-navy px-2 py-0.5 rounded disabled:opacity-40">
            {logging ? '…' : 'Log'}
          </button>
        </div>
      )}
    </div>
  );
}
